import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import i18n from '../i18n'
import { useBoundStore } from '../store/store'
import {
  fetchGameCategoriesData,
  needsCategoryRefresh,
} from '../utils/gameCategories'
import { queryKeys } from './queries/keys'
import {
  feedbackUnreadQueryKey,
  fetchFeedbackUnreadCount,
} from './queries/useFeedbackUnread'
import { fetchVipStatus } from './queries/useVipStatus'
import useKycInfo from './useKycInfo'
import useMemberInfo from './useMemberInfo'

// Namespaces the side menu (Sidebar + Tabs) translate. They are loaded on
// demand, so without preloading them the first menu open suspends to the
// app-level Suspense boundary and shows a blank/loading flash.
const SIDEBAR_NAMESPACES = ['drawer', 'kyc', 'blogs']

type IdleHandle = number | ReturnType<typeof setTimeout>

const runWhenIdle = (cb: () => void): IdleHandle => {
  if (typeof window.requestIdleCallback === 'function') {
    return window.requestIdleCallback(cb, { timeout: 2000 })
  }
  return setTimeout(cb, 200)
}

const cancelIdle = (handle: IdleHandle) => {
  if (typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(handle as number)
  } else {
    clearTimeout(handle as ReturnType<typeof setTimeout>)
  }
}

/**
 * Warms up the mobile side menu after the initial page render so the first
 * open is instant and never blanks:
 *  - preloads the drawer's i18n namespaces (prevents the first-open suspense),
 *  - prefetches the casino category list into the store (the menu reads from
 *    the store, so it no longer fires its own fetch on open), and
 *  - prefetches the logged-in user's VIP progress into the store (the menu's
 *    VIP level otherwise only fires `/vip.php` on the first open), and
 *  - prefetches the logged-in user's member info into the user store (the menu
 *    otherwise only fires `/ajax_data.php` member-info on the first open), and
 *  - prefetches the logged-in user's feedback unread count into the react-query
 *    cache (the menu's badge otherwise only fires its query on the first open),
 *    and
 *  - prefetches the logged-in user's KYC status into the user store (the menu's
 *    verify badge otherwise only fires `/conduct.php` on the first open).
 *
 * Work is deferred to idle time and fire-and-forget, so it never blocks the
 * initial render. Mount this once near the app root.
 */
const useSidebarPreload = () => {
  const username = useBoundStore(state => state.user.username)
  const setCategories = useBoundStore(state => state.casino.setCategories)
  const { refreshMemberInfo } = useMemberInfo()
  const { getKycInfo } = useKycInfo()
  const queryClient = useQueryClient()

  useEffect(() => {
    let cancelled = false

    const handle = runWhenIdle(() => {
      // i18next caches loaded namespaces; calling this repeatedly is a no-op.
      i18n.loadNamespaces(SIDEBAR_NAMESPACES).catch(() => {
        /* the menu still renders once the namespace eventually loads */
      })

      // Read the guard inputs from the store at idle-callback time, not from the
      // render snapshot closed over when the effect ran: runWhenIdle defers this
      // by up to 2s, during which another consumer (e.g. a casino page's
      // useGameCategories) may have already filled the store. Reading fresh
      // here avoids firing a duplicate fetch that just overwrites it.
      const { casino } = useBoundStore.getState()

      if (needsCategoryRefresh(casino.categories, username)) {
        fetchGameCategoriesData(username)
          .then(data => {
            if (!cancelled && data) setCategories(data)
          })
          .catch(() => {
            /* opening the menu will retry via useGameCategories */
          })
      }

      // refreshMemberInfo writes straight into the user store. The menu still
      // re-fetches on each open for freshness; warming it here means the first
      // open already has data instead of waiting on the request.
      if (username) {
        refreshMemberInfo().catch(() => {
          /* opening the menu will retry via sidebar-body's effect */
        })

        // Populate the react-query cache so the menu's unread badge has its
        // count on first open instead of firing the query then.
        queryClient
          .prefetchQuery({
            queryKey: feedbackUnreadQueryKey(username),
            queryFn: ({ signal }) => fetchFeedbackUnreadCount(signal),
            staleTime: 0,
          })
          .catch(() => {
            /* opening the menu will retry via useFeedbackUnread */
          })

        // getKycInfo writes straight into the user store. The menu still
        // re-fetches on each open for freshness; warming it here means the
        // verify badge has its status on first open instead of waiting.
        getKycInfo().catch(() => {
          /* opening the menu will retry via tabs' effect */
        })

        // Warm the new VIP status (vip_status) so the sidebar's VipCenterCard
        // shows the player's tier instantly on first open instead of the
        // loading skeleton. Replaces the legacy vip_progress preload.
        queryClient
          .prefetchQuery({
            queryKey: queryKeys.vipCenter.progress(username),
            queryFn: ({ signal }) => fetchVipStatus(signal),
          })
          .catch(() => {
            /* opening the VIP card will retry via useVipStatus */
          })
      }
    })

    return () => {
      cancelled = true
      cancelIdle(handle)
    }
    // Re-warm when the auth state flips (the category set differs for guests
    // vs. logged-in users); namespace preloading stays a no-op after the first.
  }, [username])
}

export default useSidebarPreload
